import React from 'react';
import {DateTime} from 'luxon';
import {useDateTimeFormat} from 'hooks/useConfig';
import isoTimeToLocal from 'utils/date';

interface FormattedDateTimeProps {
    dateTime: string;
    showTime?: boolean;
    options?: {
        zone?: string;
        setZone?: boolean;
    };
    fallback?: string;
}

export const FormattedDateTime: React.FC<FormattedDateTimeProps> = ({
    dateTime,
    showTime = false,
    options = {zone: 'utc'},
    fallback = ''
}) => {
    const {format, isLoaded} = useDateTimeFormat();

    if (!dateTime) {
        return <span>{fallback}</span>;
    }

    // If config is not loaded, use isoTimeToLocal with default format as fallback
    if (!isLoaded) {
        try {
            const defaultFormatted = isoTimeToLocal(dateTime, showTime, null, options);
            return <span>{defaultFormatted}</span>;
        } catch (error) {
            console.warn('Fallback date formatting error:', error);
            return <span>{fallback || dateTime}</span>;
        }
    }

    try {
        const formatted = isoTimeToLocal(dateTime, showTime, format, options);
        return <span>{formatted}</span>;
    } catch (error) {
        console.warn('Date formatting error:', error);
        return <span>{fallback || dateTime}</span>;
    }
};

// Hook for components that need the formatted date as a string
export const useFormattedDateTime = () => {
    const {format, isLoaded} = useDateTimeFormat();

    const formatDateTime = (
        dateTime: string,
        showTime: boolean = false,
        options: { zone?: string; setZone?: boolean } = {zone: 'utc'}
    ): string => {
        if (!isLoaded || !dateTime) {
            return '';
        }

        try {
            return isoTimeToLocal(dateTime, showTime, format, options);
        } catch (error) {
            console.warn('Date formatting error:', error);
            return dateTime;
        }
    };

    return {formatDateTime, format, isLoaded};
};

export const useFormattedDate = () => {
    const formatDate = (
        dateTime: string,
        options: { zone?: string; setZone?: boolean } = {setZone: false}
    ): string => {
        if (!dateTime) {
            return '';
        }

        try {
            const timestamp = DateTime.fromISO(dateTime, options);
            return timestamp.toFormat('yyyy-MM-dd');
        } catch (error) {
            console.warn('Date formatting error:', error);
            return dateTime;
        }
    };

    return {formatDate};
};
